import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { User, MapPin, Shield, Download, Trash2, Loader2, AlertTriangle, Save, ChevronRight, KeyRound, Gift } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ChangePasswordForm } from '../components/ChangePasswordForm';
import { LoyaltyPanel } from '../components/LoyaltyPanel';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AccountPage = () => {
  const { t, language } = useLanguage();
  const { user, token, logout, fetchUser } = useAuth();
  const navigate = useNavigate();

  const T = (fr, en) => (language === 'fr' ? fr : en);

  const [profile, setProfile] = useState({
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    phone: user?.phone || '',
  });
  const [savingProfile, setSavingProfile] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!user) return;
    setProfile({
      first_name: user.first_name || '',
      last_name: user.last_name || '',
      phone: user.phone || '',
    });
  }, [user]);

  const saveProfile = async () => {
    setSavingProfile(true);
    try {
      await axios.put(`${API}/users/me`, profile, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(T('Profil mis à jour', 'Profile updated'));
      if (fetchUser) await fetchUser();
    } catch (err) {
      toast.error(err.response?.data?.detail || T('Erreur de mise à jour', 'Update error'));
    } finally {
      setSavingProfile(false);
    }
  };

  const exportData = async () => {
    setExporting(true);
    try {
      const { data } = await axios.get(`${API}/users/me/export`, { headers: { Authorization: `Bearer ${token}` } });
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `respectful-markets-export-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(T('Vos données ont été exportées', 'Your data has been exported'));
    } catch (err) {
      toast.error(T('Échec de l\'export', 'Export failed'));
    } finally {
      setExporting(false);
    }
  };

  const deleteAccount = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/users/me`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(T('Compte supprimé. Au revoir.', 'Account deleted. Goodbye.'));
      logout();
      navigate('/');
    } catch (err) {
      toast.error(T('Échec de la suppression', 'Deletion failed'));
      setDeleting(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#0F4C5C]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FBF8F3]" data-testid="account-page">
      {/* Hero */}
      <div className="bg-gradient-to-br from-[#0F4C5C] to-[#0B3A47] text-white py-12">
        <div className="container-main">
          <nav className="flex items-center gap-2 text-sm text-white/70 mb-4">
            <span>{T('Accueil', 'Home')}</span>
            <ChevronRight className="w-4 h-4" />
            <span className="text-[#E9C46A]">{T('Mon compte', 'My account')}</span>
          </nav>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight">
            {T('Bonjour, ', 'Hello, ')}
            <span className="text-[#E9C46A]">{user.first_name}</span>
          </h1>
          <p className="text-white/70 mt-2">{user.email}</p>
        </div>
      </div>

      <div className="container-main py-10">
        <Tabs defaultValue="profile" className="w-full">
          <TabsList className="bg-white border border-[#0F4C5C]/10 p-1 rounded-full mb-8 h-auto">
            <TabsTrigger value="profile" className="rounded-full px-5 py-2.5 data-[state=active]:bg-[#0F4C5C] data-[state=active]:text-white" data-testid="tab-profile">
              <User className="w-4 h-4 mr-2" />
              {T('Profil', 'Profile')}
            </TabsTrigger>
            <TabsTrigger value="addresses" className="rounded-full px-5 py-2.5 data-[state=active]:bg-[#0F4C5C] data-[state=active]:text-white" data-testid="tab-addresses">
              <MapPin className="w-4 h-4 mr-2" />
              {T('Adresses', 'Addresses')}
            </TabsTrigger>
            <TabsTrigger value="loyalty" className="rounded-full px-5 py-2.5 data-[state=active]:bg-[#0F4C5C] data-[state=active]:text-white" data-testid="tab-loyalty">
              <Gift className="w-4 h-4 mr-2" />
              {T('Fidélité', 'Loyalty')}
            </TabsTrigger>
            <TabsTrigger value="security" className="rounded-full px-5 py-2.5 data-[state=active]:bg-[#0F4C5C] data-[state=active]:text-white" data-testid="tab-security">
              <KeyRound className="w-4 h-4 mr-2" />
              {T('Sécurité', 'Security')}
            </TabsTrigger>
            <TabsTrigger value="privacy" className="rounded-full px-5 py-2.5 data-[state=active]:bg-[#0F4C5C] data-[state=active]:text-white" data-testid="tab-privacy">
              <Shield className="w-4 h-4 mr-2" />
              {T('Confidentialité (RGPD)', 'Privacy (GDPR)')}
            </TabsTrigger>
          </TabsList>

          {/* PROFILE */}
          <TabsContent value="profile">
            <div className="bg-white rounded-2xl border border-[#0F4C5C]/10 p-6 md:p-8 shadow-sm max-w-2xl">
              <h2 className="text-xl font-bold text-[#0F4C5C] mb-1">{T('Informations personnelles', 'Personal information')}</h2>
              <p className="text-sm text-muted-foreground mb-6">
                {T('Modifiez votre nom, votre prénom et votre numéro de téléphone.', 'Update your name and phone number.')}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="first_name">{t('firstName')}</Label>
                  <Input
                    id="first_name"
                    value={profile.first_name}
                    onChange={(e) => setProfile({ ...profile, first_name: e.target.value })}
                    className="mt-1.5 h-11"
                    data-testid="account-first-name"
                  />
                </div>
                <div>
                  <Label htmlFor="last_name">{t('lastName')}</Label>
                  <Input
                    id="last_name"
                    value={profile.last_name}
                    onChange={(e) => setProfile({ ...profile, last_name: e.target.value })}
                    className="mt-1.5 h-11"
                    data-testid="account-last-name"
                  />
                </div>
                <div className="sm:col-span-2">
                  <Label htmlFor="phone">{T('Téléphone', 'Phone')}</Label>
                  <Input
                    id="phone"
                    value={profile.phone}
                    onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                    placeholder="+33 6 12 34 56 78"
                    className="mt-1.5 h-11"
                    data-testid="account-phone"
                  />
                </div>
                <div className="sm:col-span-2">
                  <Label>{t('email')}</Label>
                  <Input value={user.email} disabled className="mt-1.5 h-11 bg-slate-50" />
                  <p className="text-xs text-muted-foreground mt-1.5">
                    {T('L\'e-mail ne peut être modifié pour des raisons de sécurité.', 'Email cannot be changed for security reasons.')}
                  </p>
                </div>
              </div>
              <div className="mt-6 flex justify-end">
                <Button onClick={saveProfile} disabled={savingProfile} className="btn-primary" data-testid="account-save-btn">
                  {savingProfile ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                  {T('Enregistrer', 'Save')}
                </Button>
              </div>
            </div>
          </TabsContent>

          {/* LOYALTY */}
          <TabsContent value="loyalty">
            <LoyaltyPanel />
          </TabsContent>

          {/* SECURITY */}
          <TabsContent value="security">
            <div className="bg-white rounded-2xl border border-[#0F4C5C]/10 p-6 md:p-8 shadow-sm max-w-2xl">
              <h2 className="text-xl font-bold text-[#0F4C5C] mb-1">{T('Changer mon mot de passe', 'Change my password')}</h2>
              <p className="text-sm text-muted-foreground mb-6">
                {T('Choisissez un mot de passe fort et unique (8 caractères minimum).',
                   'Choose a strong, unique password (min. 8 characters).')}
              </p>
              <ChangePasswordForm />
            </div>
          </TabsContent>

          {/* ADDRESSES */}
          <TabsContent value="addresses">
            <div className="bg-white rounded-2xl border border-[#0F4C5C]/10 p-6 md:p-8 shadow-sm max-w-2xl">
              <h2 className="text-xl font-bold text-[#0F4C5C] mb-1">{T('Mes adresses', 'My addresses')}</h2>
              <p className="text-sm text-muted-foreground mb-6">
                {T('Vos adresses sont enregistrées automatiquement à chaque commande pour gagner du temps au passage en caisse.', 'Your addresses are saved automatically on each order to speed up checkout.')}
              </p>
              <div className="rounded-xl bg-[#FBF8F3] border border-dashed border-[#0F4C5C]/20 p-8 text-center">
                <MapPin className="w-10 h-10 mx-auto text-[#0F4C5C]/40 mb-3" />
                <p className="text-sm text-[#0F4C5C]/70">
                  {T('Vous pourrez gérer vos adresses depuis cet onglet après votre première commande.', 'You will be able to manage your addresses from this tab after your first order.')}
                </p>
                <Button variant="outline" className="mt-4" onClick={() => navigate('/shop')}>
                  {T('Faire une commande', 'Place an order')}
                </Button>
              </div>
            </div>
          </TabsContent>

          {/* PRIVACY / RGPD */}
          <TabsContent value="privacy">
            <div className="space-y-6 max-w-2xl">
              <div className="bg-white rounded-2xl border border-[#0F4C5C]/10 p-6 md:p-8 shadow-sm">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#2A9D8F]/10 flex items-center justify-center flex-shrink-0">
                    <Download className="w-6 h-6 text-[#2A9D8F]" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-[#0F4C5C] text-lg">{T('Exporter mes données', 'Export my data')}</h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      {T(
                        'Téléchargez l\'intégralité des données que nous détenons sur vous (profil, panier, favoris, commandes, avis) au format JSON, conformément à votre droit à la portabilité (RGPD article 20).',
                        'Download all data we hold about you (profile, cart, wishlist, orders, reviews) in JSON format, as per your right to portability (GDPR Article 20).'
                      )}
                    </p>
                    <Button onClick={exportData} disabled={exporting} className="mt-4 btn-primary" data-testid="rgpd-export-btn">
                      {exporting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Download className="w-4 h-4 mr-2" />}
                      {T('Télécharger mes données (JSON)', 'Download my data (JSON)')}
                    </Button>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl border-2 border-red-100 p-6 md:p-8 shadow-sm">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-red-50 flex items-center justify-center flex-shrink-0">
                    <Trash2 className="w-6 h-6 text-red-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-red-700 text-lg">{T('Supprimer mon compte', 'Delete my account')}</h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      {T(
                        'Action irréversible : vos informations personnelles seront anonymisées immédiatement. Vos commandes restent conservées pour respecter nos obligations comptables et légales (10 ans).',
                        'Irreversible action: your personal data will be anonymised immediately. Your orders are kept for legal and accounting obligations (10 years).'
                      )}
                    </p>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="destructive" className="mt-4" data-testid="rgpd-delete-btn">
                          <Trash2 className="w-4 h-4 mr-2" />
                          {T('Supprimer définitivement mon compte', 'Permanently delete my account')}
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle className="flex items-center gap-2">
                            <AlertTriangle className="w-5 h-5 text-red-600" />
                            {T('Êtes-vous absolument certain ?', 'Are you absolutely sure?')}
                          </AlertDialogTitle>
                          <AlertDialogDescription>
                            {T(
                              'Cette action est irréversible. Votre profil, votre panier, vos favoris et vos avis seront immédiatement anonymisés. Vous ne pourrez plus vous connecter avec cet e-mail.',
                              'This action is irreversible. Your profile, cart, wishlist and reviews will be anonymised immediately. You will no longer be able to log in with this email.'
                            )}
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={deleteAccount}
                            disabled={deleting}
                            className="bg-red-600 hover:bg-red-700"
                            data-testid="rgpd-delete-confirm-btn"
                          >
                            {deleting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                            {T('Oui, supprimer mon compte', 'Yes, delete my account')}
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              </div>

              <div className="bg-[#FBF8F3] rounded-xl border border-[#0F4C5C]/10 p-5 text-sm text-[#0F4C5C]/80">
                <p>
                  <strong>{T('Pour aller plus loin : ', 'Learn more:')}</strong>
                  {' '}
                  <button
                    type="button"
                    onClick={() => window.dispatchEvent(new CustomEvent('open-cookie-settings'))}
                    className="underline decoration-[#E9C46A] hover:text-[#E9C46A]"
                  >
                    {T('gérer mes préférences cookies', 'manage my cookie preferences')}
                  </button>
                  {' · '}
                  <a href="/privacy" className="underline decoration-[#E9C46A] hover:text-[#E9C46A]">
                    {T('lire notre politique de confidentialité', 'read our privacy policy')}
                  </a>
                </p>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};
